import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ButtonV2 from 'components/ButtonV2';
import TableSkeleton from 'components/TableSkeleton';
import {
  TableContainer,
  StyledTable,
  TableHeader,
  TableRow,
  TableCell,
  CellPrimary,
  CellSecondary,
  ActionButtons,
  MobileCardGrid,
  MobileCard,
  CardHeader,
  CardHeaderContent,
  CardInfo,
  CardTitle,
  CardSubtitle,
  ExpandIcon,
  CardDetails,
  CardDetailsContent,
  DetailRow,
  DetailLabel,
  DetailValue,
  MobileActions,
  MobileActionButton,
  EmptyState,
  Pagination,
  PageEllipsis,
  SectionHeader,
  SectionTitle,
  MobileQuickInfo,
  MobileQuickInfoItem,
} from './StyledComponents';

// Common Action Icons
const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const MessageIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
  </svg>
);

const CreditIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const DuplicateIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

// Default action icon mapping - extensible for common action types
const defaultActionIcons = {
  view: EyeIcon,
  edit: EditIcon,
  delete: DeleteIcon,
  message: MessageIcon,
  credit: CreditIcon,
  download: DownloadIcon,
  duplicate: DuplicateIcon,
};

/**
 * DataTable - A flexible, reusable table component
 *
 * @param {Array} columns - Column definitions with field, header, render, etc.
 * @param {Array} data - Array of data objects to display
 * @param {boolean} loading - Show skeleton loading state
 * @param {string} emptyMessage - Message when no data
 * @param {string} emptySubtext - Subtext for empty state
 * @param {node} emptyIcon - Custom icon for empty state
 * @param {Array} actions - Action buttons configuration
 * @param {string} keyField - Field to use as unique key (default: 'id')
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Page change handler
 * @param {function} mobileCardRenderer - Custom mobile card renderer
 * @param {number} skeletonRows - Number of skeleton rows to show
 * @param {string} title - Optional section title above the table
 * @param {function} actionsRenderer - Custom actions renderer (receives item, returns JSX)
 * @param {function} mobileActionsRenderer - Custom mobile actions renderer
 * @param {Array} mobileQuickInfo - Fields to show in mobile quick info section
 */
const DataTable = ({
  columns,
  data,
  loading,
  emptyMessage = 'No data found',
  emptySubtext = 'Try adjusting your search or filters',
  emptyIcon,
  actions = [],
  keyField = 'id',
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  mobileCardRenderer,
  skeletonRows = 5,
  title,
  actionsRenderer,
  mobileActionsRenderer,
  mobileQuickInfo,
}) => {
  // Ref for scroll-to-top on page change
  const tableRef = useRef(null);

  // State for expandable mobile cards
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCard = (itemId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    if (onPageChange) {
      // Scroll BEFORE triggering page change to avoid layout shift issues
      if (tableRef.current) {
        tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Delay the page change slightly to allow scroll to start
      setTimeout(() => {
        onPageChange(page);
      }, 100);
    }
  };

  // Render a single action button
  const renderActionButton = (action, item, index) => {
    // Get the icon - can be a preset type, custom component, or JSX
    const IconComponent =
      action.icon || defaultActionIcons[action.type] || EyeIcon;
    const iconElement =
      typeof IconComponent === 'function' ? <IconComponent /> : IconComponent;

    // Resolve disabled - can be a boolean or function that receives item
    const isDisabled =
      typeof action.disabled === 'function'
        ? action.disabled(item)
        : action.disabled;

    // Build button props
    const buttonProps = {
      key: index,
      variant: action.variant || 'icon',
      icon: iconElement,
      title: action.title || action.type,
      disabled: isDisabled,
    };

    // Handle click or link. `to` may be a path, a location object, or a
    // function of the row returning either (or a falsy value to fall back
    // to onClick). Links render as real <a href>s so open-in-new-tab works.
    const linkTo =
      typeof action.to === 'function' ? action.to(item) : action.to;
    if (linkTo) {
      buttonProps.as = Link;
      buttonProps.to = linkTo;
    } else if (action.onClick) {
      buttonProps.onClick = () => action.onClick(item);
    }

    const button = <ButtonV2 {...buttonProps} />;

    // Wrap with wrapper component if provided (e.g., ComingSoonModal)
    if (action.wrapper) {
      const WrapperComponent = action.wrapper;
      const wrapperProps =
        typeof action.wrapperProps === 'function'
          ? action.wrapperProps(item)
          : action.wrapperProps || {};
      return (
        <WrapperComponent key={index} {...wrapperProps}>
          {button}
        </WrapperComponent>
      );
    }

    return button;
  };

  // Render action buttons (default implementation)
  const renderActions = (item) => {
    // Use custom renderer if provided
    if (actionsRenderer) {
      return actionsRenderer(item);
    }

    return (
      <ActionButtons>
        {actions.map((action, index) =>
          renderActionButton(action, item, index)
        )}
      </ActionButtons>
    );
  };

  // Render mobile actions - uses MobileActionButton with labels
  const renderMobileActions = (item) => {
    if (mobileActionsRenderer) {
      return mobileActionsRenderer(item);
    }

    return actions.map((action, index) => {
      // Get the icon - can be a preset type, custom component, or JSX
      const IconComponent =
        action.icon || defaultActionIcons[action.type] || EyeIcon;
      const iconElement =
        typeof IconComponent === 'function' ? <IconComponent /> : IconComponent;

      // Resolve disabled - can be a boolean or function that receives item
      const isDisabled =
        typeof action.disabled === 'function'
          ? action.disabled(item)
          : action.disabled;

      // Get label for mobile
      const label = action.mobileLabel || action.title || action.type;

      // Build button props
      const buttonProps = {
        key: index,
        disabled: isDisabled,
      };

      // Handle click or link (see desktop renderer above)
      const linkTo =
        typeof action.to === 'function' ? action.to(item) : action.to;
      if (linkTo) {
        buttonProps.as = Link;
        buttonProps.to = linkTo;
      } else if (action.onClick) {
        buttonProps.onClick = () => action.onClick(item);
      }

      const button = (
        <MobileActionButton {...buttonProps}>
          {iconElement}
          {label}
        </MobileActionButton>
      );

      // Wrap with wrapper component if provided (e.g., ComingSoonModal)
      if (action.wrapper) {
        const WrapperComponent = action.wrapper;
        const wrapperProps =
          typeof action.wrapperProps === 'function'
            ? action.wrapperProps(item)
            : action.wrapperProps || {};
        return (
          <WrapperComponent key={index} {...wrapperProps}>
            {button}
          </WrapperComponent>
        );
      }

      return button;
    });
  };

  // Render cell content based on column config
  const renderCellContent = (item, column) => {
    if (column.render) {
      return column.render(item);
    }

    const value = item[column.field];

    if (column.subField) {
      const subValue = item[column.subField];
      return (
        <>
          <CellPrimary hasSubtext={!!subValue}>{value}</CellPrimary>
          {subValue && <CellSecondary>{subValue}</CellSecondary>}
        </>
      );
    }

    return value;
  };

  // Loading state - show skeleton
  if (loading) {
    return (
      <>
        {title && (
          <SectionHeader>
            <SectionTitle>{title}</SectionTitle>
          </SectionHeader>
        )}
        <TableSkeleton
          rows={skeletonRows}
          columns={columns.length}
          showActions={actions.length > 0 || !!actionsRenderer}
          actionsCount={actions.length || 3}
        />
      </>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <>
        {title && (
          <SectionHeader>
            <SectionTitle>{title}</SectionTitle>
          </SectionHeader>
        )}
        <EmptyState>
          {emptyIcon || (
            <svg
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ marginBottom: '16px', opacity: 0.5 }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          )}
          <div
            style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}
          >
            {emptyMessage}
          </div>
          <div style={{ fontSize: '14px', opacity: 0.8 }}>{emptySubtext}</div>
        </EmptyState>
      </>
    );
  }

  // Default mobile card renderer - Expandable design
  const defaultMobileCardRenderer = (item) => {
    const firstColumn = columns[0];
    const otherColumns = columns.slice(1);
    const itemId = item[keyField];
    const isExpanded = expandedCards[itemId];

    return (
      <MobileCard key={itemId} expanded={isExpanded}>
        <CardHeader onClick={() => toggleCard(itemId)}>
          <CardHeaderContent>
            <CardInfo>
              <CardTitle>
                {firstColumn.render
                  ? firstColumn.render(item)
                  : item[firstColumn.field]}
              </CardTitle>
              {firstColumn.subField && item[firstColumn.subField] && (
                <CardSubtitle>{item[firstColumn.subField]}</CardSubtitle>
              )}
            </CardInfo>
            <ExpandIcon
              expanded={isExpanded}
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
            </ExpandIcon>
          </CardHeaderContent>
        </CardHeader>

        <CardDetails expanded={isExpanded}>
          <CardDetailsContent>
            {/* Mobile Quick Info section (compact grid display) */}
            {mobileQuickInfo && mobileQuickInfo.length > 0 && (
              <MobileQuickInfo>
                {mobileQuickInfo.map((info) => (
                  <MobileQuickInfoItem key={info.field}>
                    <DetailLabel>{info.label}</DetailLabel>
                    <DetailValue>
                      {info.render
                        ? info.render(item)
                        : item[info.field] || '—'}
                    </DetailValue>
                  </MobileQuickInfoItem>
                ))}
              </MobileQuickInfo>
            )}

            {/* Standard detail rows for columns not in quick info */}
            {!mobileQuickInfo &&
              otherColumns.map((column) => (
                <DetailRow key={column.field}>
                  <DetailLabel>{column.header}</DetailLabel>
                  <DetailValue>
                    {column.render
                      ? column.render(item)
                      : item[column.field] || '—'}
                  </DetailValue>
                </DetailRow>
              ))}
          </CardDetailsContent>

          {(actions.length > 0 || actionsRenderer || mobileActionsRenderer) && (
            <MobileActions>{renderMobileActions(item)}</MobileActions>
          )}
        </CardDetails>
      </MobileCard>
    );
  };

  return (
    <div ref={tableRef}>
      {/* Section Title */}
      {title && (
        <SectionHeader>
          <SectionTitle>{title}</SectionTitle>
        </SectionHeader>
      )}

      {/* Desktop Table */}
      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              {columns.map((column) => (
                <TableHeader key={column.field} style={column.headerStyle}>
                  {column.header}
                </TableHeader>
              ))}
              {(actions.length > 0 || actionsRenderer) && (
                <TableHeader style={{ textAlign: 'right' }}>
                  Actions
                </TableHeader>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <TableRow key={item[keyField]}>
                {columns.map((column) => (
                  <TableCell key={column.field} style={column.cellStyle}>
                    {renderCellContent(item, column)}
                  </TableCell>
                ))}
                {(actions.length > 0 || actionsRenderer) && (
                  <TableCell>{renderActions(item)}</TableCell>
                )}
              </TableRow>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>

      {/* Mobile Cards */}
      <MobileCardGrid>
        {data.map((item) =>
          mobileCardRenderer
            ? mobileCardRenderer(item, renderActions, renderMobileActions)
            : defaultMobileCardRenderer(item)
        )}
      </MobileCardGrid>

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <Pagination>
          <ButtonV2
            variant="icon"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            icon={
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            }
            title="Previous page"
          />
          {getPageNumbers().map((page, index) =>
            page === '...' ? (
              <PageEllipsis key={`ellipsis-${index}`}>...</PageEllipsis>
            ) : (
              <ButtonV2
                variant="icon"
                key={page}
                active={page === currentPage}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </ButtonV2>
            )
          )}
          <ButtonV2
            variant="icon"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            icon={
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            }
            title="Next page"
          />
        </Pagination>
      )}
    </div>
  );
};

DataTable.propTypes = {
  /** Column definitions */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      subField: PropTypes.string,
      render: PropTypes.func,
      headerStyle: PropTypes.object,
      cellStyle: PropTypes.object,
    })
  ).isRequired,
  /** Data array to display */
  data: PropTypes.array,
  /** Show loading skeleton */
  loading: PropTypes.bool,
  /** Empty state message */
  emptyMessage: PropTypes.string,
  /** Empty state subtext */
  emptySubtext: PropTypes.string,
  /** Custom empty state icon */
  emptyIcon: PropTypes.node,
  /**
   * Action buttons configuration
   * Each action can have:
   * - type: 'view' | 'edit' | 'delete' | 'message' | 'credit' | custom
   * - title: Tooltip/label text
   * - icon: Custom icon component or JSX
   * - onClick: Click handler (receives item)
   * - to: Link path (string or function that receives item)
   * - wrapper: Wrapper component (e.g., ComingSoonModal)
   * - wrapperProps: Props for wrapper (object or function that receives item)
   * - variant: Button variant for desktop (default: 'icon')
   * - mobileVariant: Button variant for mobile (default: 'secondary')
   * - mobileLabel: Label text for mobile button
   * - disabled: Boolean or function (receives item) to disable the action
   */
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
      onClick: PropTypes.func,
      to: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      wrapper: PropTypes.elementType,
      wrapperProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
      variant: PropTypes.string,
      mobileVariant: PropTypes.string,
      mobileLabel: PropTypes.string,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    })
  ),
  /** Field to use as unique key */
  keyField: PropTypes.string,
  /** Current page number */
  currentPage: PropTypes.number,
  /** Total number of pages */
  totalPages: PropTypes.number,
  /** Page change handler */
  onPageChange: PropTypes.func,
  /** Custom mobile card renderer - receives (item, renderActions, renderMobileActions) */
  mobileCardRenderer: PropTypes.func,
  /** Number of skeleton rows */
  skeletonRows: PropTypes.number,
  /** Section title above the table */
  title: PropTypes.string,
  /** Custom actions renderer - receives item, returns JSX */
  actionsRenderer: PropTypes.func,
  /** Custom mobile actions renderer - receives item, returns JSX */
  mobileActionsRenderer: PropTypes.func,
  /** Mobile quick info fields for compact display */
  mobileQuickInfo: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
    })
  ),
};

export default DataTable;

// Export styled components for custom usage
export {
  TableCard,
  TableCardHeader,
  TableCardAccent,
  TableCardTitle,
  TableContainer,
  StyledTable,
  TableHeader,
  TableRow,
  TableCell,
  CellPrimary,
  CellSecondary,
  ActionButtons,
  MobileCardGrid,
  MobileCard,
  CardHeader,
  CardHeaderContent,
  CardInfo,
  CardTitle,
  CardSubtitle,
  ExpandIcon,
  CardDetails,
  CardDetailsContent,
  DetailRow,
  DetailLabel,
  DetailValue,
  MobileActions,
  MobileActionButton,
  EmptyState,
  LoadingState,
  Pagination,
  PageEllipsis,
  SectionHeader,
  SectionTitle,
  MobileQuickInfo,
  MobileQuickInfoItem,
} from './StyledComponents';

// Export common action icons for reuse
export {
  EyeIcon,
  EditIcon,
  DeleteIcon,
  MessageIcon,
  CreditIcon,
  DownloadIcon,
  DuplicateIcon,
};
